/**
 * Fehler-Normalisierung für supabase-js.
 *
 * WARUM: supabase-js wirft standardmässig NICHT (`shouldThrowOnError` = false,
 * `.throwOnError()` wird nirgends benutzt). Es liefert `{ data, error }`, wobei
 * `error` ein PLAIN OBJECT ist (PostgrestError = { message, code, details, hint })
 * — KEINE Error-Instanz. Ein `throw error` im Hook landet daher als Plain Object
 * im catch, und der verbreitete Ausdruck
 *   `e instanceof Error ? e.message : 'Fehler'`
 * fällt IMMER auf 'Fehler' zurück.
 *
 * (Auf die Fehlerüberwachung hat die Normalisierung hier KEINE Wirkung:
 * captureError() läuft nur in der ErrorBoundary, und Fehler aus
 * Datenzugriffen erreichen sie nie — React Query fängt sie vorher ab.)
 *
 * Nutzung: an der Hook-Grenze mit toSupabaseError() normalisieren,
 * in der UI mit supabaseErrorMessage() ausgeben.
 */

/** Teil-Shape von PostgrestError / AuthError / StorageError. */
type SupabaseErrorLike = {
  message?: unknown;
  code?: unknown;
  details?: unknown;
  hint?: unknown;
};

export class SupabaseError extends Error {
  readonly code: string | null;
  readonly details: string | null;
  readonly hint: string | null;

  constructor(source: SupabaseErrorLike) {
    super(
      typeof source.message === 'string' && source.message
        ? source.message
        : 'Unbekannter Supabase-Fehler',
    );
    this.name = 'SupabaseError';
    this.code = typeof source.code === 'string' ? source.code : null;
    this.details = typeof source.details === 'string' ? source.details : null;
    this.hint = typeof source.hint === 'string' ? source.hint : null;
  }
}

/** Normalisiert alles Geworfene zu einer echten Error-Instanz. */
export function toSupabaseError(e: unknown): Error {
  if (e instanceof Error) return e;
  if (e && typeof e === 'object') return new SupabaseError(e as SupabaseErrorLike);
  return new Error(typeof e === 'string' && e ? e : 'Unbekannter Fehler');
}

export function supabaseErrorCode(e: unknown): string | null {
  if (e instanceof SupabaseError) return e.code;
  if (e && typeof e === 'object' && 'code' in e) {
    const c = (e as SupabaseErrorLike).code;
    if (typeof c === 'string') return c;
  }
  return null;
}

/** Postgres-SQLSTATE / PostgREST-Code → deutsche Klartext-Meldung. */
const CODE_MESSAGES: Readonly<Record<string, string>> = {
  '23505': 'Dafür existiert bereits ein Eintrag.',
  '23P01': 'Der Zeitraum überschneidet sich mit einem bestehenden Eintrag.',
  '23514': 'Die Eingabe verletzt eine Gültigkeitsregel.',
  '23503': 'Der Eintrag wird noch an anderer Stelle verwendet.',
  // Schreibversuch auf eine GENERATED-Spalte — präzise Diagnose für das
  // Rollout-Fenster von Migration 0038 (Kompat-Spalte `date`).
  '428C9': 'Dieses Feld wird automatisch berechnet und kann nicht gesetzt werden.',
  '42501': 'Keine Berechtigung für diese Aktion.',
  PGRST116: 'Mehrdeutiges Ergebnis — bitte Daten prüfen.',
  PGRST301: 'Sitzung abgelaufen. Bitte neu anmelden.',
};

/**
 * Lesbare Meldung aus Error, PostgrestError-Plain-Object oder Unbekanntem.
 * `overrides` erlaubt kontextspezifische Texte pro SQLSTATE.
 */
export function supabaseErrorMessage(
  e: unknown,
  fallback = 'Unbekannter Fehler',
  overrides?: Readonly<Record<string, string>>,
): string {
  const code = supabaseErrorCode(e);
  if (code) {
    const mapped = overrides?.[code] ?? CODE_MESSAGES[code];
    if (mapped) return mapped;
  }
  if (e instanceof Error && e.message) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as SupabaseErrorLike).message;
    if (typeof m === 'string' && m) return m;
  }
  return fallback;
}
